// Normalises the conversation endpoints into the shape these cards render.
//
//   GET /assistants/:id/conversations            list, under { data, pagination }
//   GET /assistants/:id/conversations/:id        one record, + leadScore/leadScoreLabel
//   GET /assistants/:id/conversations/:id/messages   the transcript
//
// A list row looks like:
//   { id, title, source, status, isLead, leadName, leadEmail, leadPhone,
//     leadSummary, tags,
//     visitorId, ipAddress, userAgent, pageUrl, utmSource, createdAt,
//     updatedAt, leadScore, leadScoreLabel, lastMessage: { role, content } }
//
// Note `lastMessage` is an object, and the lead fields are flat on the record
// rather than nested under a `lead` key.

import { statusStyles, avatarColors } from "./data";

const COLORS = Object.keys(avatarColors);
const KNOWN_STATUSES = Object.keys(statusStyles);

const firstOf = (source, keys) => {
  if (!source) return null;

  for (const key of keys) {
    const value = source[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }

  return null;
};

export const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "?";

// Stable per conversation, so a row keeps its colour across refetches.
const colorFor = (key) => {
  const text = String(key || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash + text.charCodeAt(i)) % COLORS.length;
  }
  return COLORS[hash];
};

// The badge keys off exact labels like "Hot lead"; this maps a looser API
// value onto one of them and title-cases anything unrecognised.
const normalizeStatus = (value) => {
  if (!value) return null;

  const text = String(value).replace(/[-_]/g, " ").trim();
  return (
    KNOWN_STATUSES.find((known) => known.toLowerCase() === text.toLowerCase()) ||
    text.charAt(0).toUpperCase() + text.slice(1)
  );
};

export const relativeTime = (value) => {
  if (!value) return "";

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return String(value);

  const seconds = Math.round((Date.now() - then.getTime()) / 1000);
  if (seconds < 60) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.round(hours / 24)}d ago`;
};

const longDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const dateAndTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
};

const clockTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// All the record gives us about the device is the raw user agent.
const deviceFrom = (userAgent) => {
  if (!userAgent) return "";
  return /Mobi|Android|iPhone|iPad/i.test(userAgent) ? "Mobile" : "Desktop";
};

// Just the host, so a long file:// or query-string URL doesn't overflow.
const shortUrl = (value) => {
  if (!value) return "";
  try {
    const url = new URL(value);
    return url.protocol === "file:"
      ? value.split(/[\\/]/).pop() || value
      : `${url.hostname}${url.pathname === "/" ? "" : url.pathname}`;
  } catch {
    return String(value);
  }
};

/**
 * `lastMessage` is `{ role, content }` — passing it through as the preview
 * handed React an object to render, which threw.
 */
const messageText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.content || value.text || value.message || "";
};

// LeadInfoPanel and ConversationDetail read straight off `lead`, so it is
// always an object — missing fields render blank instead of throwing.
const mapLead = (raw) => {
  // The API keeps these flat on the conversation; a nested object is still
  // accepted in case the detail endpoint groups them.
  const nested = firstOf(raw, ["lead", "visitor", "contact", "leadInfo"]) || {};
  const qualified = firstOf(nested, ["qualified", "answers", "qualification"]);

  return {
    // One line saying what the visitor actually wants, written when the
    // assistant hands them to the team. Null on every other conversation,
    // including all of them from before the field existed, so the views that
    // show it leave the line out rather than printing a placeholder.
    summary:
      firstOf(raw, ["leadSummary"]) || firstOf(nested, ["summary"]) || "",
    email: firstOf(raw, ["leadEmail"]) || firstOf(nested, ["email"]) || "",
    phone: firstOf(raw, ["leadPhone"]) || firstOf(nested, ["phone"]) || "",
    company: firstOf(nested, ["company", "organisation", "organization"]) || "",
    jobTitle: firstOf(nested, ["jobTitle", "title", "role"]) || "",
    location: firstOf(raw, ["location"]) || firstOf(nested, ["location", "city"]) || "",
    website: firstOf(nested, ["website", "site", "domain"]) || "",
    score: firstOf(raw, ["leadScore"]) ?? firstOf(nested, ["score"]) ?? "",
    firstSeen: dateAndTime(firstOf(raw, ["createdAt", "startedAt"])),
    sourcePage: shortUrl(firstOf(raw, ["pageUrl", "sourcePage", "page"])),
    // Empty rather than a dash: whether to show a placeholder is the view's
    // decision, and a literal "—" here reads as real data to anything that
    // checks the field.
    utmSource: firstOf(raw, ["utmSource"]) || "",
    device: deviceFrom(firstOf(raw, ["userAgent"])),
    qualified:
      qualified && typeof qualified === "object" && !Array.isArray(qualified)
        ? qualified
        : {},
  };
};

/**
 * Each message carries an explicit `role`, so the thread can tell the two
 * sides apart without comparing the sender's name to a hardcoded one.
 */
const mapMessage = (raw, visitorName, assistantName) => {
  const rawRole = String(
    firstOf(raw, ["role", "sender", "author", "from"]) || "",
  ).toLowerCase();

  const isAssistant =
    rawRole === "assistant" || rawRole === "bot" || rawRole === "ai";
  // "owner" is what the live socket calls a message the account owner typed
  // after joining the conversation; it is the same thing as an agent reply.
  const isAgent =
    rawRole === "agent" ||
    rawRole === "owner" ||
    rawRole === "human" ||
    rawRole === "you";

  return {
    role: isAssistant ? "assistant" : isAgent ? "agent" : "visitor",
    sender: isAssistant ? assistantName : isAgent ? "You" : visitorName,
    time: clockTime(firstOf(raw, ["time", "createdAt", "timestamp", "sentAt"])),
    text: messageText(raw),
  };
};

/**
 * @param raw           the conversation record
 * @param assistantName used to label assistant-side messages
 * @param transcript    messages from the separate /messages endpoint; they
 *                      take precedence over anything on the record
 */
export const mapConversation = (
  raw,
  assistantName = "Assistant",
  transcript,
) => {
  if (!raw) return null;

  const id = firstOf(raw, ["id", "_id", "conversationId"]);

  // `title` is "New chat" for every record, so it isn't a usable name.
  const name =
    firstOf(raw, ["leadName", "visitorName", "contactName", "name"]) ||
    "Visitor";

  const messages = firstOf(raw, ["messages", "transcript", "items"]);
  const tags = firstOf(raw, ["tags", "labels", "topics"]);

  const source =
    Array.isArray(transcript) && transcript.length
      ? transcript
      : Array.isArray(messages)
        ? messages
        : [];

  const mapped = source.map((message) =>
    mapMessage(message, name, assistantName),
  );

  return {
    id,
    name,
    initials: initialsOf(name),
    color: colorFor(id || name),
    // `status: "open"` is the conversation's state, not the visitor's presence.
    online: false,
    time: relativeTime(firstOf(raw, ["updatedAt", "lastMessageAt", "createdAt"])),
    // The same moment unformatted, for callers that render it their own way.
    updatedAt: firstOf(raw, ["updatedAt", "lastMessageAt", "createdAt"]) || null,
    date: longDate(firstOf(raw, ["createdAt", "startedAt", "date"])),
    preview:
      messageText(firstOf(raw, ["lastMessage", "preview", "summary", "snippet"])) ||
      mapped[mapped.length - 1]?.text ||
      "",
    tags: Array.isArray(tags) ? tags.map(String) : [],
    // leadScoreLabel is the lead-quality badge the design shows. `status` is
    // open/closed, which isn't what that badge means.
    status: normalizeStatus(
      firstOf(raw, ["leadScoreLabel", "category", "status"]),
    ),
    ended: String(firstOf(raw, ["status"]) || "").toLowerCase() === "closed",
    // "widget" for a real visitor, "training" for the owner's own test chats.
    source: String(firstOf(raw, ["source", "channel"]) || "").toLowerCase(),
    lead: mapLead(raw),
    messages: mapped,
  };
};

export const mapConversations = (list, assistantName) =>
  (Array.isArray(list) ? list : []).map((item) =>
    mapConversation(item, assistantName),
  );
